<div class="schedule-tab">
  <div class="two-column-layout">
    <!-- Left Column: Chart and Table -->
    <div class="left-column">
      <div class="section-header-row">
        <h3 class="section-header">Hours</h3>
        <div class="hours-date-selector-container">
          <i *ngIf="isLoadingChartData" class="pi pi-spin pi-spinner loading-spinner"></i>
          <p-calendar
            [(ngModel)]="selectedHoursDate"
            (ngModelChange)="onHoursDateChange()"
            (onSelect)="onHoursDateSelect()"
            [disabled]="isLoadingChartData"
            dateFormat="mm/dd/yy"
            [showIcon]="true"
            appendTo="body"
            ariaLabel="Select hours date"
          ></p-calendar>
        </div>
      </div>
      <div class="chart-container">
        <div class="chart-wrapper">
          <div class="chart-area">
            <canvas
              baseChart
              [type]="barChartType"
              [data]="barChartData"
              [options]="barChartOptions"
            >
            </canvas>
          </div>
          <div class="duration-summary">
            <div class="duration-item">
              <span class="duration-value">{{ getTotalDuration("Off") }}</span>
              <span class="status-label off">Off</span>
            </div>
            <div class="duration-item">
              <span class="duration-value">{{ getTotalDuration("Sleeper") }}</span>
              <span class="status-label sleeper">Sleeper</span>
            </div>
            <div class="duration-item">
              <span class="duration-value">{{ getTotalDuration("Driving") }}</span>
              <span class="status-label driving">Driving</span>
            </div>
            <div class="duration-item">
              <span class="duration-value">{{ getTotalDuration("On") }}</span>
              <span class="status-label on">On</span>
            </div>
          </div>
        </div>
      </div>

      <h3 class="section-header">Duty Status Log</h3>
      <div class="duty-status-table-container">
        <table class="duty-status-table">
          <thead>
            <tr>
              <th>DATE</th>
              <th>START TIME</th>
              <th>END TIME</th>
              <th>EVENT TYPE</th>
            </tr>
          </thead>
          <tbody>
            <ng-container *ngFor="let group of getGroupedRecordsByDate()">
              <tr *ngFor="let record of group.records; let first = first">
                <td *ngIf="first" [attr.rowspan]="group.records.length">
                  {{ group.date | date: "MM/dd/yyyy" }}
                </td>
                <td>{{ formatTime(record.start) }}</td>
                <td>{{ formatTime(record.end) }}</td>
                <td>
                  <span
                    class="duty-status-pill"
                    [ngStyle]="{ 'background-color': getDutyStatusColor(record.status) }"
                  >
                    {{ record.status | uppercase }}
                  </span>
                </td>
              </tr>
            </ng-container>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Right Column: Calendar -->
    <div class="right-column">
      <h3 class="section-header">PTO Eligibility</h3>
      <div class="pto-eligibility-container">
        <div style="display: flex; flex-direction: row; gap: 30px">
          <div class="pto-status-wrapper">
            <span class="pto-status">
              {{ ptoEligibilityStatusText }}
            </span>
          </div>
          <div>
            <p-checkbox
              [(ngModel)]="ptoOverrideValue"
              [binary]="true"
              inputId="ptoOverride"
              [disabled]="!isEditMode"
              (onChange)="onFieldChange()"
            >
            </p-checkbox>
            <label for="ptoOverride" class="pto-eligibility-label">Override</label>
          </div>
          <div *ngIf="ptoOverrideValue">
            <p-checkbox
              [(ngModel)]="ptoEligibleValue"
              [binary]="true"
              inputId="ptoEligible"
              [disabled]="!isEditMode"
              (onChange)="onFieldChange()"
            >
            </p-checkbox>
            <label for="ptoEligible" class="pto-eligibility-label">Driver Is PTO Eligible</label>
          </div>
        </div>
      </div>

      <h3 class="section-header">Calendar</h3>
      <div class="calendar-container">
        <p-calendar
          [(ngModel)]="selectedDate"
          [inline]="true"
          [showWeek]="false"
          (onSelect)="onCalendarSelect($event)"
          ariaLabel="Driver schedule calendar"
        >
          <ng-template pTemplate="date" let-date>
            <span
              [ngStyle]="getDateStyle(date)"
              class="calendar-date"
              [class.has-event]="hasEventsOnDate(date)"
              [class.first-day-back]="isFirstDayBack(date)"
              [class.projected-restart]="isProjected34HourRestart(date)"
              [pTooltip]="getDateTooltip(date)"
              [tooltipDisabled]="
                !hasEventsOnDate(date) && !isFirstDayBack(date) && !isProjected34HourRestart(date)
              "
              tooltipPosition="top"
              (click)="onCalendarDateClick($event)"
            >
              {{ date.day }}
            </span>
          </ng-template>
        </p-calendar>
      </div>

      <div class="time-off-header">
        <h3 class="section-header">Time Off Events</h3>
        <div class="time-off-header-actions">
          <button
            class="btn btn-outline-secondary upload-schedule-btn"
            (click)="showUploadDialog()"
          >
            <i class="fa fa-upload"></i> Upload Schedule
          </button>
          <button class="btn btn-primary add-event-btn" (click)="showAddDialog()">
            <i class="fa fa-plus"></i> Add Event
          </button>
        </div>
      </div>

      <!-- Undo Banner -->
      <div *ngIf="showUndoBanner" class="undo-banner">
        <span>
          <i class="fa fa-info-circle"></i>
          Schedule was uploaded. You can undo if needed.
        </span>
        <div class="undo-banner-actions">
          <button class="btn btn-sm btn-warning" (click)="undoUpload()">
            <i class="fa fa-undo"></i> Undo Upload
          </button>
          <button class="btn btn-sm btn-default" (click)="dismissUndoBanner()">Dismiss</button>
        </div>
      </div>

      <div class="time-off-table-container">
        <p-table
          [value]="timeOffRecords"
          tableStyleClass="time-off-table"
          responsiveLayout="scroll"
          [sortMode]="'single'"
        >
          <ng-template pTemplate="header">
            <tr>
              <th pSortableColumn="startDate">
                <div class="table-header-cell">
                  START DATE
                  <p-sortIcon field="startDate"></p-sortIcon>
                </div>
              </th>
              <th pSortableColumn="endDate">
                <div class="table-header-cell">
                  END DATE
                  <p-sortIcon field="endDate"></p-sortIcon>
                </div>
              </th>
              <th pSortableColumn="eventType">
                <div class="table-header-cell">
                  EVENT TYPE
                  <p-sortIcon field="eventType"></p-sortIcon>
                </div>
              </th>
              <th pSortableColumn="notes">
                <div class="table-header-cell">
                  NOTES
                  <p-sortIcon field="notes"></p-sortIcon>
                </div>
              </th>
              <th pSortableColumn="changeCount">
                <div class="table-header-cell">
                  CHANGES
                  <p-sortIcon field="changeCount"></p-sortIcon>
                </div>
              </th>
              <th class="actions-column"></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-record>
            <tr [class.template-event-row]="isTemplateEvent(record)">
              <td>{{ record.startDate | date: "MM/dd/yyyy" }}</td>
              <td>{{ record.endDate | date: "MM/dd/yyyy" }}</td>
              <td>
                <span *ngIf="isTemplateEvent(record)" class="template-badge">
                  {{ record.eventType }}
                </span>
                <span *ngIf="!isTemplateEvent(record)">{{ record.eventType }}</span>
              </td>
              <td>{{ record.notes }}</td>
              <td>
                <span
                  class="change-count-badge"
                  [class.has-changes]="record.changeCount && record.changeCount > 0"
                  (click)="
                    record.changeCount && record.changeCount > 0 ? viewChangeHistory(record) : null
                  "
                  [title]="
                    record.changeCount && record.changeCount > 0
                      ? 'Click to view change history'
                      : 'No changes'
                  "
                >
                  {{ record.changeCount || 0 }}
                </span>
              </td>
              <td class="actions-cell">
                <i
                  *ngIf="!isTemplateEvent(record)"
                  class="fa fa-pencil action-icon edit-icon"
                  (click)="editTimeOffRecord(record)"
                  title="Edit"
                ></i>
                <i
                  *ngIf="!isTemplateEvent(record)"
                  class="fa fa-trash action-icon delete-icon"
                  (click)="deleteTimeOffRecord(record)"
                  title="Delete"
                ></i>
                <i
                  *ngIf="isTemplateEvent(record)"
                  class="fa fa-times-circle action-icon clear-batch-icon"
                  (click)="clearBatch(record)"
                  title="Remove all events from this template upload"
                ></i>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="empty-message">No time off records found.</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </div>
  </div>

  <!-- Add/Edit Time Off Dialog -->
  <p-dialog
    [(visible)]="displayDialog"
    [header]="isEditingRecord ? 'Edit Time Off Event' : 'Add Time Off Event'"
    [modal]="true"
    [style]="{ width: '500px' }"
    [draggable]="false"
    [resizable]="false"
  >
    <div class="dialog-content">
      <div class="form-row">
        <div class="form-group half-width">
          <label for="startDate">Start Date <span class="required">*</span></label>
          <p-calendar
            [(ngModel)]="newTimeOffRecord.startDate"
            dateFormat="mm/dd/yy"
            [showIcon]="true"
            [showOnFocus]="false"
            [appendTo]="'body'"
            inputId="startDate"
          >
          </p-calendar>
        </div>

        <div class="form-group half-width">
          <label for="endDate">End Date <span class="required">*</span></label>
          <p-calendar
            [(ngModel)]="newTimeOffRecord.endDate"
            dateFormat="mm/dd/yy"
            [showIcon]="true"
            [showOnFocus]="false"
            [appendTo]="'body'"
            inputId="endDate"
          >
          </p-calendar>
        </div>
      </div>

      <div class="form-group">
        <label for="eventType">Event Type <span class="required">*</span></label>
        <p-dropdown
          [(ngModel)]="newTimeOffRecord.eventType"
          [options]="eventTypeOptions"
          optionLabel="label"
          optionValue="value"
          optionDisabled="disabled"
          placeholder="Select Event Type"
          inputId="eventType"
          [style]="{ width: '100%' }"
        >
        </p-dropdown>
      </div>

      <div class="form-group">
        <label for="notes">Notes <span class="required">*</span></label>
        <textarea
          id="notes"
          class="form-control"
          [(ngModel)]="newTimeOffRecord.notes"
          rows="3"
          placeholder="Enter notes"
          required
        >
        </textarea>
      </div>

      <!-- Change History Section -->
      <div
        *ngIf="isEditingRecord && newTimeOffRecord.id && eventChanges.length > 0"
        class="change-history-section"
      >
        <p-accordion [multiple]="false" [activeIndex]="[]">
          <p-accordionTab header="Change History ({{ eventChanges.length }})">
            <div class="changes-list-compact">
              <div *ngFor="let change of eventChanges" class="change-item-compact">
                <div class="change-header-compact">
                  <span class="change-date-compact">{{
                    change.dateCreatedUtc | date: "MM/dd/yy HH:mm"
                  }}</span>
                  <span class="change-reason-compact">{{
                    getChangeReasonLabel(change.reason)
                  }}</span>
                </div>
                <div class="change-dates-compact">
                  <span [class.changed]="!areDatesEqual(change.previousStart, change.newStart)">{{
                    change.newStart | date: "MM/dd/yy"
                  }}</span>
                  <span class="separator">-</span>
                  <span [class.changed]="!areDatesEqual(change.previousEnd, change.newEnd)">{{
                    change.newEnd | date: "MM/dd/yy"
                  }}</span>
                </div>
                <div *ngIf="change.reasonNote" class="change-note-compact">
                  {{ change.reasonNote }}
                </div>
              </div>
              <!-- Original Value -->
              <div *ngIf="getOriginalChange()" class="change-item-compact original-item">
                <div class="change-header-compact">
                  <span class="change-date-compact">{{
                    newTimeOffRecord.dateCreated | date: "MM/dd/yy HH:mm"
                  }}</span>
                  <span class="change-reason-original">Original</span>
                </div>
                <div class="change-dates-compact">
                  <span>{{ getOriginalChange().previousStart | date: "MM/dd/yy" }}</span>
                  <span class="separator">-</span>
                  <span>{{ getOriginalChange().previousEnd | date: "MM/dd/yy" }}</span>
                </div>
              </div>
            </div>
          </p-accordionTab>
        </p-accordion>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <button class="btn btn-default" (click)="closeDialog()">Cancel</button>
      <button class="btn btn-primary" (click)="saveTimeOffRecord()">Save</button>
    </ng-template>
  </p-dialog>

  <!-- Change Reason Dialog -->
  <p-dialog
    [(visible)]="displayChangeDialog"
    header="Date Change Reason"
    [modal]="true"
    [style]="{ width: '450px' }"
    [draggable]="false"
    [resizable]="false"
  >
    <div class="dialog-content">
      <p class="change-warning">
        <i class="fa fa-exclamation-triangle"></i>
        You have changed the dates for this event. Please provide a reason for the change.
      </p>

      <div class="form-group">
        <label for="changeReason">Reason <span class="required">*</span></label>
        <p-dropdown
          [(ngModel)]="changeReason"
          [options]="changeReasonOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Select Reason"
          inputId="changeReason"
          [style]="{ width: '100%' }"
        >
        </p-dropdown>
      </div>

      <div class="form-group">
        <label for="changeReasonNote">Note (Optional)</label>
        <textarea
          id="changeReasonNote"
          class="form-control"
          [(ngModel)]="changeReasonNote"
          rows="3"
          placeholder="Enter additional details (optional)"
        >
        </textarea>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <button class="btn btn-default" (click)="cancelChangeReason()">Cancel</button>
      <button class="btn btn-primary" (click)="confirmChangeReason()">Confirm</button>
    </ng-template>
  </p-dialog>

  <!-- Change History Dialog -->
  <p-dialog
    [(visible)]="displayChangesHistoryDialog"
    header="Change History"
    [modal]="true"
    [style]="{ width: '700px' }"
    [draggable]="false"
    [resizable]="false"
  >
    <div class="dialog-content">
      <div *ngIf="eventChanges.length === 0" class="no-changes">No change history available.</div>
      <div *ngIf="eventChanges.length > 0" class="changes-list">
        <div *ngFor="let change of eventChanges" class="change-item">
          <div class="change-header">
            <span class="change-date">{{ change.dateCreatedUtc | date: "MM/dd/yyyy HH:mm" }}</span>
            <span class="change-reason">{{ getChangeReasonLabel(change.reason) }}</span>
          </div>
          <div class="change-details">
            <div class="change-row">
              <span class="change-label">Previous:</span>
              <span class="change-value">
                {{ change.previousStart | date: "MM/dd/yyyy" }} -
                {{ change.previousEnd | date: "MM/dd/yyyy" }}
              </span>
            </div>
            <div class="change-row">
              <span class="change-label">New:</span>
              <span class="change-value">
                {{ change.newStart | date: "MM/dd/yyyy" }} -
                {{ change.newEnd | date: "MM/dd/yyyy" }}
              </span>
            </div>
            <div *ngIf="change.reasonNote" class="change-row">
              <span class="change-label">Note:</span>
              <span class="change-value">{{ change.reasonNote }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <button class="btn btn-primary" (click)="closeChangesHistoryDialog()">Close</button>
    </ng-template>
  </p-dialog>

  <!-- Upload Schedule Dialog -->
  <p-dialog
    [(visible)]="displayUploadDialog"
    [header]="uploadPreview ? 'Review Schedule Upload' : 'Upload Schedule Template'"
    [modal]="true"
    [style]="{ width: '720px' }"
    [draggable]="false"
    [resizable]="false"
  >
    <div class="dialog-content">
      <!-- File Selection (shown when no preview yet) -->
      <div *ngIf="!uploadPreview && !isUploadParsing">
        <p class="upload-instructions">
          Select a Vertex42 yearly calendar (.xlsx) with time-off dates highlighted in
          <span class="green-highlight">green</span>.
        </p>
        <div class="upload-file-input">
          <input
            type="file"
            accept=".xlsx,.xls"
            (change)="onFileSelected($event)"
            class="form-control"
            id="scheduleFile"
            aria-label="Upload schedule file"
          />
        </div>
      </div>

      <!-- Loading -->
      <div *ngIf="isUploadParsing" class="upload-loading">
        <i class="pi pi-spin pi-spinner" style="font-size: 2em"></i>
        <p>Parsing spreadsheet...</p>
      </div>

      <!-- Preview -->
      <div *ngIf="uploadPreview && !isUploadParsing">
        <!-- Driver Name Mismatch Warning -->
        <div *ngIf="uploadPreview.driverNameMismatch" class="upload-warning">
          <i class="fa fa-exclamation-triangle"></i>
          <div>
            <strong>Driver name mismatch</strong>
            <p>
              Spreadsheet: <strong>{{ uploadPreview.spreadsheetDriverName }}</strong
              ><br />
              Driver: <strong>{{ uploadPreview.driverName }}</strong>
            </p>
            <p class="mismatch-note">
              This may be due to name variations. Review carefully before confirming.
            </p>
          </div>
        </div>

        <!-- Driver Name Match -->
        <div
          *ngIf="!uploadPreview.driverNameMismatch && uploadPreview.spreadsheetDriverName"
          class="upload-match"
        >
          <i class="fa fa-check-circle"></i>
          Driver name matches: <strong>{{ uploadPreview.driverName }}</strong>
        </div>

        <!-- Summary -->
        <div class="upload-summary">
          <div class="summary-row">
            <span class="summary-label">Year:</span>
            <span class="summary-value">{{ uploadPreview.spreadsheetYear || "Unknown" }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Time-off blocks:</span>
            <span class="summary-value">{{ uploadPreview.dateRanges.length }}</span>
          </div>
          <div class="summary-row">
            <span class="summary-label">Total days off:</span>
            <span class="summary-value">{{ uploadPreview.totalDaysOff }}</span>
          </div>
        </div>

        <!-- Year Calendar -->
        <div class="preview-year-calendar">
          <!-- Legend -->
          <div class="preview-legend">
            <span class="preview-legend-item">
              <span class="preview-legend-swatch preview-timeoff"></span> Time Off
            </span>
            <span *ngIf="uploadPreview.conflicts.length > 0" class="preview-legend-item">
              <span class="preview-legend-swatch preview-conflict"></span> Conflicts with existing
              events
            </span>
          </div>

          <!-- 12-month grid -->
          <div class="preview-months-grid">
            <div class="preview-month" *ngFor="let month of getPreviewCalendarMonths()">
              <div class="preview-month-name">{{ month.label }}</div>
              <div class="preview-dow-headers">
                <span>S</span><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span
                ><span>S</span>
              </div>
              <div class="preview-week" *ngFor="let week of month.weeks">
                <span
                  *ngFor="let day of week"
                  [class]="getPreviewDayClass(day)"
                  [title]="day ? (day | date: 'MMM d, yyyy') : ''"
                  >{{ day ? day.getDate() : "" }}</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ng-template pTemplate="footer">
      <button class="btn btn-default" (click)="closeUploadDialog()">Cancel</button>
      <button
        *ngIf="uploadPreview"
        class="btn btn-primary"
        (click)="confirmUpload()"
        [disabled]="isUploadConfirming"
      >
        <i *ngIf="isUploadConfirming" class="pi pi-spin pi-spinner"></i>
        {{ isUploadConfirming ? "Uploading..." : "Confirm Upload" }}
      </button>
    </ng-template>
  </p-dialog>

  <!-- Confirmation Dialog -->
  <p-confirmDialog></p-confirmDialog>
</div>
